import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Calendar } from 'lucide-react';
import { apiClient, StrategistReportContractV1, StrategistSymbolItemV1 } from '../../api/client';
import { CRYPTO_BILINGUAL, CARD_TITLES_BILINGUAL } from '../../shared/constants/bilingualTitles';
import './CryptoStrategistView.css';

export const MAX_FRESHNESS_SECONDS = 7;
const MAX_RETENTION_ENTRIES = 32;

// Module-level bounded persistent latch map surviving navigation / remounts
const globalReportLatches = new Map<string, Set<string>>();

function setBoundedMapEntry<K, V>(map: Map<K, V>, key: K, value: V): void {
  if (!map.has(key) && map.size >= MAX_RETENTION_ENTRIES) {
    const oldestKey = map.keys().next().value;
    if (oldestKey !== undefined) {
      map.delete(oldestKey);
    }
  }
  map.set(key, value);
}

/**
 * Pure string-to-BigInt decimal comparator (zero floating-point arithmetic on prices).
 * Returns -1 if a < b, 0 if a == b, 1 if a > b, or null if malformed.
 */
function compareDecimalStrings(a: string, b: string): number | null {
  const cleanA = a.trim();
  const cleanB = b.trim();
  if (!/^\d+(\.\d+)?$/.test(cleanA) || !/^\d+(\.\d+)?$/.test(cleanB)) {
    return null;
  }
  const [intA, fracA = ''] = cleanA.split('.');
  const [intB, fracB = ''] = cleanB.split('.');
  const scale = Math.max(fracA.length, fracB.length);
  const scaledA = BigInt(intA + fracA.padEnd(scale, '0'));
  const scaledB = BigInt(intB + fracB.padEnd(scale, '0'));
  if (scaledA < scaledB) return -1;
  if (scaledA > scaledB) return 1;
  return 0;
}

const HIGH_VALUE_SYMBOLS = new Set([
  'BTCUSDT',
  'ETHUSDT',
  'SOLUSDT',
  'BNBUSDT',
  'OKBUSDT',
  'BTC',
  'ETH',
  'SOL',
  'BNB',
  'OKB',
]);

const MEME_SYMBOLS = new Set([
  'PEPEUSDT',
  'SHIBUSDT',
  'BONKUSDT',
  'FLOKIUSDT',
  'PEPE',
  'SHIB',
]);

/**
 * Formats incoming decimal string for UI presentation strictly adhering to project rules:
 * - High-value coins (price >= 10 or HIGH_VALUE_SYMBOLS, e.g. BTC, ETH, SOL, BNB, OKB): 2 decimal places with thousands separators.
 * - Small-value coins (price < 10, e.g. BGB, HYPE): strictly 4 decimal places.
 * - Meme coins (price < 0.0001 or MEME_SYMBOLS): 8 decimal places.
 * - Pure BigInt & string processing (zero binary floating-point drift).
 */
function formatPresentationPrice(
  rawVal: string | null | undefined,
  symbol?: string,
  options?: { withCommas?: boolean }
): string {
  if (!rawVal || rawVal === '--') return '--';
  const cleaned = rawVal.replace(/,/g, '').trim();
  if (!cleaned || !/^-?\d+(\.\d+)?$/.test(cleaned)) return rawVal;

  const isNeg = cleaned.startsWith('-');
  const absStr = isNeg ? cleaned.slice(1) : cleaned;
  const parts = absStr.split('.');
  const intPart = parts[0] || '0';
  const decPart = parts[1] || '';

  const symUpper = symbol ? symbol.toUpperCase() : '';
  const withCommas = options?.withCommas ?? true;

  // 1. Meme coins: < 0.0001 or MEME_SYMBOLS -> strictly 8 decimals
  const isMeme = MEME_SYMBOLS.has(symUpper) || (BigInt(intPart) === 0n && /^0000/.test(decPart));
  if (isMeme) {
    if (decPart.length <= 8) {
      const formattedInt = withCommas ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : intPart;
      const res = `${formattedInt}.${decPart.padEnd(8, '0')}`;
      return isNeg ? `-${res}` : res;
    }
    const scaled9 = BigInt(intPart) * 1000000000n + BigInt(decPart.slice(0, 9).padEnd(9, '0'));
    const rounded = (scaled9 + 5n) / 10n;
    const rInt = String(rounded / 100000000n);
    const rDec = String(rounded % 100000000n).padStart(8, '0');
    const formattedInt = withCommas ? rInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : rInt;
    const res = `${formattedInt}.${rDec}`;
    return isNeg ? `-${res}` : res;
  }

  // 2. High-value coins: >= 10 or HIGH_VALUE_SYMBOLS -> 2 decimals with thousands separators
  const isExplicitSmall = symUpper === 'BGBUSDT' || symUpper === 'BGB';
  const isHighVal = !isExplicitSmall && (HIGH_VALUE_SYMBOLS.has(symUpper) || BigInt(intPart) >= 10n);

  if (isHighVal) {
    if (decPart.length <= 2) {
      const formattedInt = withCommas ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : intPart;
      const padded = decPart ? `${formattedInt}.${decPart.padEnd(2, '0')}` : `${formattedInt}.00`;
      return isNeg ? `-${padded}` : padded;
    }
    const scaled3 = BigInt(intPart) * 1000n + BigInt(decPart.slice(0, 3).padEnd(3, '0'));
    const rounded = (scaled3 + 5n) / 10n;
    const rInt = String(rounded / 100n);
    const rDec = String(rounded % 100n).padStart(2, '0');
    const formattedInt = withCommas ? rInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : rInt;
    const res = `${formattedInt}.${rDec}`;
    return isNeg ? `-${res}` : res;
  }

  // 3. Small-value coins (price < 10, e.g. BGB, HYPE): strictly 4 decimal places
  if (decPart.length <= 4) {
    const formattedInt = withCommas ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : intPart;
    const padded = decPart ? `${formattedInt}.${decPart.padEnd(4, '0')}` : `${formattedInt}.0000`;
    return isNeg ? `-${padded}` : padded;
  }
  const scaled5 = BigInt(intPart) * 100000n + BigInt(decPart.slice(0, 5).padEnd(5, '0'));
  const rounded = (scaled5 + 5n) / 10n;
  const rInt = String(rounded / 10000n);
  const rDec = String(rounded % 10000n).padStart(4, '0');
  const formattedInt = withCommas ? rInt.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : rInt;
  const res = `${formattedInt}.${rDec}`;
  return isNeg ? `-${res}` : res;
}

interface LiveQuoteObservation {
  price: string;
  timestamp_utc: string;
}

export interface HydratedIndicators {
  symbol: string;
  anchor_bar_close_utc: string;
  status: string;
  stale: boolean;
  atr14?: string | null;
  ma20?: string | null;
  ma50?: string | null;
  swingHigh20?: string | null;
  swingLow20?: string | null;
  benchmarkClose?: string | null;
}

export type CadenceId = '6h' | '3h' | '1h';

export interface CadenceOption {
  id: CadenceId;
  label: string;
  shortLabel: string;
  hours: number;
  sessionsPerDay: number;
  status: 'active' | 'ready';
  description: string;
}

export const CADENCE_OPTIONS: CadenceOption[] = [
  {
    id: '6h',
    label: '6H 战略定调 (4期/日)',
    shortLabel: '6H 战略',
    hours: 6,
    sessionsPerDay: 4,
    status: 'active',
    description: '现行生效 · 适合大波段中轨与多空分水岭确立',
  },
  {
    id: '3h',
    label: '3H 波段捕捉 (8期/日 - 预留)',
    shortLabel: '3H 波段',
    hours: 3,
    sessionsPerDay: 8,
    status: 'ready',
    description: '架构就绪 · 兼顾跨时区流动性转换与突破确认',
  },
  {
    id: '1h',
    label: '1H 战术雷达 (24期/日 - 预留)',
    shortLabel: '1H 雷达',
    hours: 1,
    sessionsPerDay: 24,
    status: 'ready',
    description: '架构就绪 · 高频捕捉小级别假突破与量价异动',
  },
];

export interface SessionSlot {
  id: string;
  index: number;
  startHour: number;
  endHour: number;
  label: string;
  publishedAtUtc: string;
  status: 'archived' | 'active' | 'pending';
}

const CORE_SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'OKBUSDT', 'BGBUSDT', 'HYPEUSDT'];

const SYMBOL_META: Record<
  string,
  {
    icon: string;
    category: string;
  }
> = {
  BTCUSDT: {
    icon: '₿',
    category: '宏观锚点 / 主导',
  },
  ETHUSDT: {
    icon: 'Ξ',
    category: '智能合约生态',
  },
  SOLUSDT: {
    icon: '◎',
    category: '高吞吐公链',
  },
  BNBUSDT: {
    icon: '⬡',
    category: '平台与生态',
  },
  OKBUSDT: {
    icon: '◈',
    category: 'CEX 平台代币',
  },
  BGBUSDT: {
    icon: '◆',
    category: '衍生品与生态',
  },
  HYPEUSDT: {
    icon: '▲',
    category: '去中心化永续',
  },
};

export const CryptoStrategistView: React.FC = () => {
  const [report, setReport] = useState<StrategistReportContractV1 | null>(null);
  const [uiState, setUiState] = useState<'loading' | 'fresh' | 'stale' | 'empty' | 'disconnected'>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(() => Date.now());
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  const [liveQuotes, setLiveQuotes] = useState<Record<string, LiveQuoteObservation>>({});
  const [isDocHidden, setIsDocHidden] = useState<boolean>(
    () => typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );

  // New Architectural States: Cadence, Selected Symbol, Selected Date, View Mode
  const [selectedCadence, setSelectedCadence] = useState<CadenceId>('6h');
  const [selectedSymbol, setSelectedSymbol] = useState<string>('BTCUSDT');
  const [viewMode, setViewMode] = useState<'deep_dive' | 'matrix'>('deep_dive');
  const [indicatorsMap, setIndicatorsMap] = useState<Record<string, HydratedIndicators>>({});

  // Hydrate quantitative 4H indicators (ATR-14, SMA 20/50) with strict report anchor verification
  useEffect(() => {
    let isCancelled = false;

    const loadIndicators = async () => {
      const anchor_bar_close_utc = report?.anchor_bar_close_utc;
      if (!anchor_bar_close_utc || report?.status !== 'fresh' || uiState !== 'fresh') {
        if (!isCancelled) {
          setIndicatorsMap({});
        }
        return;
      }

      const newMap: Record<string, HydratedIndicators> = {};
      await Promise.all(
        CORE_SYMBOLS.map(async (sym) => {
          try {
            const matrixRes = (await apiClient.getIndicatorsMatrix(sym)) as any;
            if (!matrixRes || matrixRes.symbol !== sym || matrixRes.stale || matrixRes.status === 'STALE' || matrixRes.status === 'stale') return;
            const cell4h = matrixRes?.cells?.['4h'];
            if (
              !cell4h ||
              cell4h.symbol !== sym ||
              cell4h.timeframe !== '4h' ||
              cell4h.bar_close_utc !== anchor_bar_close_utc ||
              cell4h.status !== 'computed' ||
              cell4h.stale
            ) {
              return;
            }

            // Exact string-based decimal formatting without binary float conversions
            const formatDec = (val: string | null | undefined, dp: number): string | null => {
              if (!val || typeof val !== 'string') return null;
              const trimmed = val.trim();
              if (!/^-?(0|[1-9]\d*)(\.\d+)?$/.test(trimmed)) return null;
              const isNeg = trimmed.startsWith('-');
              const unsigned = isNeg ? trimmed.slice(1) : trimmed;
              const [intPart, fracPart = ''] = unsigned.split('.');
              if (dp === 0) return (isNeg ? '-' : '') + intPart;
              const truncatedFrac = fracPart.slice(0, dp).padEnd(dp, '0');
              return (isNeg ? '-' : '') + intPart + '.' + truncatedFrac;
            };

            const dp = sym === 'BGBUSDT' ? 4 : sym === 'BTCUSDT' || sym === 'ETHUSDT' || sym === 'BNBUSDT' ? 2 : 4;
            const ma20 = formatDec(cell4h.ma20, dp);
            const ma50 = formatDec(cell4h.ma50, dp);
            const benchmarkClose = formatDec(cell4h.close_price, dp);
            const atr14: string | null = cell4h.atr_14 ? formatDec(cell4h.atr_14, dp) : null;
            // 20B Swing H/L must not borrow unapproved 30-bar fib levels; leave null until genuine facts exist
            const swingHigh20: string | null = null;
            const swingLow20: string | null = null;

            newMap[sym] = {
              symbol: cell4h.symbol,
              anchor_bar_close_utc,
              status: cell4h.status,
              stale: !!cell4h.stale,
              atr14,
              ma20,
              ma50,
              swingHigh20,
              swingLow20,
              benchmarkClose,
            };
          } catch {
            // fail-soft
          }
        })
      );

      if (!isCancelled) {
        setIndicatorsMap(newMap);
      }
    };

    loadIndicators();
    const interval = setInterval(loadIndicators, 30000);
    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [report?.anchor_bar_close_utc, report?.status, uiState]);

  // Real-time ticking interval for continuous quote ageing & countdowns
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  // Track document visibility
  useEffect(() => {
    const onVisibilityChange = () => {
      setIsDocHidden(document.visibilityState === 'hidden');
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // 5s quote observation polling path (/api/v1/market/realtime)
  const fetchRealtimeObservations = useCallback(async () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    try {
      const res: any = await apiClient.getRealtimePrices('SPOT');
      const rawList: any[] = Array.isArray(res?.prices)
        ? res.prices
        : res?.symbols && typeof res.symbols === 'object'
        ? Object.values(res.symbols)
        : [];
      if (rawList.length > 0) {
        const nextQuotes: Record<string, LiveQuoteObservation> = {};
        for (const entry of rawList) {
          if (!entry || typeof entry !== 'object') continue;
          const sym = entry.symbol;
          const p = entry.last_price || entry.price;
          const ts = entry.timestamp_utc || entry.timestamp;
          if (typeof sym === 'string' && typeof p === 'string' && p.trim()) {
            nextQuotes[sym] = {
              price: p.trim(),
              timestamp_utc: typeof ts === 'string' ? ts.trim() : new Date().toISOString(),
            };
          }
        }
        if (Object.keys(nextQuotes).length > 0) {
          setLiveQuotes((prev) => ({ ...prev, ...nextQuotes }));
        }
      }
    } catch {
      // Non-blocking observation refresh error
    }
  }, []);

  useEffect(() => {
    fetchRealtimeObservations();
    const interval = window.setInterval(fetchRealtimeObservations, 5000);
    return () => {
      window.clearInterval(interval);
    };
  }, [fetchRealtimeObservations]);

  // Primary brief polling path (/api/v1/market/crypto/strategist/brief)
  const fetchReport = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const res = await apiClient.getStrategistBrief();
      setReport(res);
      setFetchTimestamp(Date.now());
      setErrorMessage(null);

      if (res.status === 'fresh') {
        setUiState('fresh');
      } else if (res.status === 'stale') {
        setUiState('stale');
      } else if (res.status === 'empty') {
        setUiState('empty');
      } else if (res.status === 'disconnected') {
        setUiState('disconnected');
      } else {
        setUiState('loading');
      }
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to fetch strategist brief');
      setUiState('disconnected');
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchReport();
    const interval = window.setInterval(fetchReport, 15000);
    return () => {
      window.clearInterval(interval);
    };
  }, [fetchReport]);

  // Keyboard shortcut listener: Left/Right arrows to switch symbol
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        setSelectedSymbol((prev) => {
          const idx = CORE_SYMBOLS.indexOf(prev);
          const nextIdx = (idx - 1 + CORE_SYMBOLS.length) % CORE_SYMBOLS.length;
          return CORE_SYMBOLS[nextIdx];
        });
      } else if (e.key === 'ArrowRight') {
        setSelectedSymbol((prev) => {
          const idx = CORE_SYMBOLS.indexOf(prev);
          const nextIdx = (idx + 1) % CORE_SYMBOLS.length;
          return CORE_SYMBOLS[nextIdx];
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Cadence Config resolution
  const activeCadenceConfig = useMemo(() => {
    return CADENCE_OPTIONS.find((c) => c.id === selectedCadence) || CADENCE_OPTIONS[0];
  }, [selectedCadence]);

  // Current active slot derived from actual report window or current hour
  const currentSessionSlotId = useMemo(() => {
    const windowTimeStr =
      report?.window_start_utc ||
      (report?.window_id && !Number.isNaN(Date.parse(report.window_id)) ? report.window_id : null);
    if (windowTimeStr) {
      const d = new Date(windowTimeStr);
      if (!Number.isNaN(d.getTime())) {
        const h = d.getUTCHours();
        const slotIdx = Math.floor(h / activeCadenceConfig.hours);
        return `S${slotIdx + 1}`;
      }
    }
    if (report?.anchor_bar_close_utc) {
      const d = new Date(report.anchor_bar_close_utc);
      const h = d.getUTCHours();
      const slotIdx = Math.floor(h / activeCadenceConfig.hours);
      return `S${slotIdx + 1}`;
    }
    const nowH = new Date(nowMs).getUTCHours();
    return `S${Math.floor(nowH / activeCadenceConfig.hours) + 1}`;
  }, [report?.window_start_utc, report?.window_id, report?.anchor_bar_close_utc, activeCadenceConfig.hours, nowMs]);

  // Dynamic Session slots generator based on cadence
  const sessionSlots = useMemo<SessionSlot[]>(() => {
    const hours = activeCadenceConfig.hours;
    const totalSlots = 24 / hours;
    const slots: SessionSlot[] = [];

    for (let i = 0; i < totalSlots; i++) {
      const startH = i * hours;
      const endH = (i + 1) * hours;
      const startStr = `${String(startH).padStart(2, '0')}:00`;
      const endStr = `${String(endH).padStart(2, '0')}:00`;
      const pubStr = `${String(endH % 24).padStart(2, '0')}:05 UTC`;
      const slotId = `S${i + 1}`;
      const isCurrent = slotId === currentSessionSlotId;

      slots.push({
        id: slotId,
        index: i + 1,
        startHour: startH,
        endHour: endH,
        label: `${startStr} - ${endStr} UTC`,
        publishedAtUtc: pubStr,
        status: isCurrent ? 'active' : 'pending',
      });
    }
    return slots;
  }, [activeCadenceConfig, currentSessionSlotId]);


  const isLocallyTacticsExpired = useMemo(() => {
    const tacticsExpiryMs = report?.tactics_expiry_utc ? Date.parse(report.tactics_expiry_utc) : NaN;
    return (!Number.isNaN(tacticsExpiryMs) && nowMs >= tacticsExpiryMs) || report?.tactics_lifecycle === 'EXPIRED';
  }, [report?.tactics_expiry_utc, report?.tactics_lifecycle, nowMs]);

  const isSymbolEffectiveActionable = useCallback(
    (sym: string, itm: StrategistSymbolItemV1 | undefined): boolean => {
      if (!itm || !itm.tactical_setup_eligible || itm.tactical_action === 'STAND_ASIDE') return false;
      if (uiState !== 'fresh' || report?.status !== 'fresh') return false;
      if (itm.status !== 'fresh') return false;
      if (isLocallyTacticsExpired) return false;
      if (itm.sentinel_status === 'INVALIDATED') return false;

      const reportKey = report ? `${report.report_id}:${report.window_id}` : 'default';
      const latchedSet = globalReportLatches.get(reportKey);
      if (latchedSet?.has(sym)) return false;

      // Check current live quote for stop breach with strict quote freshness validation
      const liveObs = liveQuotes[sym];
      if (liveObs && liveObs.price && liveObs.timestamp_utc && itm.invalidation_stop) {
        const parsedLive = Date.parse(liveObs.timestamp_utc);
        if (!Number.isNaN(parsedLive)) {
          const currentMs = typeof nowMs === 'number' ? nowMs : Date.now();
          const ageMs = currentMs - parsedLive;
          if (ageMs >= 0 && ageMs <= 7000) {
            const cmp = compareDecimalStrings(liveObs.price, itm.invalidation_stop);
            if (cmp !== null) {
              if (itm.trend_direction === 'LONG' && cmp <= 0) {
                if (!globalReportLatches.has(reportKey)) {
                  setBoundedMapEntry(globalReportLatches, reportKey, new Set<string>());
                }
                globalReportLatches.get(reportKey)?.add(sym);
                return false;
              } else if (itm.trend_direction === 'SHORT' && cmp >= 0) {
                if (!globalReportLatches.has(reportKey)) {
                  setBoundedMapEntry(globalReportLatches, reportKey, new Set<string>());
                }
                globalReportLatches.get(reportKey)?.add(sym);
                return false;
              }
            }
          }
        }
      }

      return true;
    },
    [uiState, report, isLocallyTacticsExpired, liveQuotes]
  );

  // Extract actionable setups count strictly derived from effective status
  const { activeSetupsCount } = useMemo(() => {
    const symbolsObj = report?.symbols || {};
    let count = 0;

    for (const [sym, itm] of Object.entries(symbolsObj)) {
      if (isSymbolEffectiveActionable(sym, itm)) {
        count++;
      }
    }

    return {
      activeSetupsCount: count,
    };
  }, [report, isSymbolEffectiveActionable]);

  const renderTelemetryBadge = () => {
    switch (uiState) {
      case 'fresh':
        return (
          <span className="telemetry-pill fresh font-mono" title="行情与策略处于实时健康就绪状态">
            <span className="status-dot green pulse" />
            <span>实时就绪 FRESH</span>
          </span>
        );
      case 'stale':
        return (
          <span className="telemetry-pill stale font-mono" title="行情生产者采样延时，处于陈旧展示状态">
            <span className="status-dot amber" />
            <span>时效延迟 STALE</span>
          </span>
        );
      case 'empty':
        return (
          <span className="telemetry-pill empty font-mono" title="策略中枢正在初始化或样本准备中">
            <span className="status-dot slate" />
            <span>初始化 EMPTY</span>
          </span>
        );
      case 'disconnected':
        return (
          <span className="telemetry-pill disconnected font-mono" title="与服务通信断开，处于离线哨兵保护状态">
            <span className="status-dot red pulse" />
            <span>网络中断 DISCONNECTED</span>
          </span>
        );
      default:
        return (
          <span className="telemetry-pill loading font-mono" title="策略与因果几何计算中">
            <span className="status-dot blue spin" />
            <span>推演中 LOADING</span>
          </span>
        );
    }
  };

  const renderBanner = () => {
    if (uiState === 'stale' || report?.status === 'stale') {
      return (
        <div className="strategist-banner stale font-mono">
          <span>▲ <strong>时效降级预警：</strong>行情生产者采样物化时间超过阈值。为严防假信号，战术执行指令已自动降级为只读观望。</span>
        </div>
      );
    }
    if (uiState === 'disconnected' || report?.status === 'disconnected') {
      return (
        <div className="strategist-banner disconnected font-mono">
          <span>🔴 <strong>传输断开熔断：</strong>{errorMessage || '无法连接 Market-Hub 策略引擎。当前所有标的强制进入离线风控防守状态。'}</span>
        </div>
      );
    }
    return null;
  };

  // Evaluation logic for any symbol item
  const evaluateSymbolItem = (symbol: string, item?: StrategistSymbolItemV1) => {
    const meta = SYMBOL_META[symbol] || {
      icon: '◇',
      category: '加密数字资产',
      macro1D: '大级别箱体整理，MA200 稳固支撑',
      meso4H: '4H 均线带收窄，等待右侧放量',
      micro1H: '1H 级别量能收敛，维持防守规约',
    };

    const hydrated = indicatorsMap[symbol];
    const isHydratedValid =
      uiState === 'fresh' &&
      report?.status === 'fresh' &&
      hydrated &&
      hydrated.symbol === symbol &&
      hydrated.anchor_bar_close_utc === report?.anchor_bar_close_utc &&
      hydrated.status === 'computed' &&
      !hydrated.stale;

    const effectiveAtr14 = item?.atr_14 || (isHydratedValid ? hydrated?.atr14 : null) || null;
    const effectiveMa20 = item?.ma_20 || (isHydratedValid ? hydrated?.ma20 : null) || null;
    const effectiveMa50 = item?.ma_50 || (isHydratedValid ? hydrated?.ma50 : null) || null;
    const effectiveSwingHigh = item?.swing_high_20 || (isHydratedValid ? hydrated?.swingHigh20 : null) || null;
    const effectiveSwingLow = item?.swing_low_20 || (isHydratedValid ? hydrated?.swingLow20 : null) || null;
    const effectiveBenchmarkPrice = item?.spot_price || (isHydratedValid ? hydrated?.benchmarkClose : null) || null;

    if (!item) {
      return {
        meta,
        effectiveSpotPrice: null,
        effectiveBenchmarkPrice,
        effectiveAtr14,
        effectiveMa20,
        effectiveMa50,
        effectiveSwingHigh,
        effectiveSwingLow,
        displayAction: 'STAND_ASIDE',
        actionCn: '空仓观望',
        ribbonClass: 'stand-aside',
        badgeClass: 'offline',
        badgeText: 'UNMONITORED_OFFLINE',
        badgeCn: '防守待机',
        thesis: '当前标的暂无可用 6H 策略报告，体系严格执行资本保护，禁止开仓。',
        isSuppressed: true,
        driftText: '--',
      };
    }

    const reportKey = report ? `${report.report_id}:${report.window_id}` : 'default';
    if (!globalReportLatches.has(reportKey)) {
      setBoundedMapEntry(globalReportLatches, reportKey, new Set<string>());
    }
    const reportLatchedSymbols = globalReportLatches.get(reportKey)!;

    const liveObs = liveQuotes[symbol];
    const effectiveSpotPrice = liveObs?.price || item.spot_price;

    let liveSourceMs: number | null = null;
    if (liveObs?.timestamp_utc) {
      const parsedLive = Date.parse(liveObs.timestamp_utc);
      if (!Number.isNaN(parsedLive)) {
        liveSourceMs = parsedLive;
      }
    }

    let snapshotSourceMs: number | null = null;
    if (item.quote_timestamp_utc) {
      const parsedItemTs = Date.parse(item.quote_timestamp_utc);
      if (!Number.isNaN(parsedItemTs)) {
        snapshotSourceMs = parsedItemTs;
      }
    }
    if (
      snapshotSourceMs === null &&
      report?.generated_at_utc &&
      item.quote_age_seconds !== null &&
      item.quote_age_seconds !== undefined
    ) {
      const parsedGen = Date.parse(report.generated_at_utc);
      if (!Number.isNaN(parsedGen)) {
        snapshotSourceMs = parsedGen - item.quote_age_seconds * 1000;
      }
    }

    const elapsedSinceFirstSeenMs = Math.max(0, nowMs - fetchTimestamp);
    const fallbackAgeMs =
      item.quote_age_seconds !== null && item.quote_age_seconds !== undefined
        ? item.quote_age_seconds * 1000 + elapsedSinceFirstSeenMs
        : null;

    const maxFreshnessMs = MAX_FRESHNESS_SECONDS * 1000;
    let isFutureQuote = false;
    let isStaleQuote = false;
    let isObservationValidAndFresh = false;

    if (liveSourceMs !== null) {
      const elapsedLiveMs = nowMs - liveSourceMs;
      isFutureQuote = elapsedLiveMs < 0;
      isStaleQuote = elapsedLiveMs > maxFreshnessMs;
      isObservationValidAndFresh = elapsedLiveMs >= 0 && elapsedLiveMs <= maxFreshnessMs;
    } else {
      const snapshotElapsedMs = snapshotSourceMs !== null ? nowMs - snapshotSourceMs : null;
      const effectiveSnapshotMs =
        snapshotElapsedMs !== null
          ? fallbackAgeMs !== null
            ? Math.max(snapshotElapsedMs, fallbackAgeMs)
            : snapshotElapsedMs
          : fallbackAgeMs;
      isFutureQuote = snapshotElapsedMs !== null && snapshotElapsedMs < 0;
      isStaleQuote = effectiveSnapshotMs !== null && effectiveSnapshotMs > maxFreshnessMs;
      isObservationValidAndFresh =
        effectiveSnapshotMs !== null && effectiveSnapshotMs >= 0 && effectiveSnapshotMs <= maxFreshnessMs;
    }

    let stopBreached = item.sentinel_status === 'INVALIDATED';
    if (
      !stopBreached &&
      isObservationValidAndFresh &&
      item.tactical_setup_eligible &&
      effectiveSpotPrice &&
      item.invalidation_stop
    ) {
      const cmp = compareDecimalStrings(effectiveSpotPrice, item.invalidation_stop);
      if (cmp !== null) {
        if (item.trend_direction === 'LONG' && cmp <= 0) {
          stopBreached = true;
        } else if (item.trend_direction === 'SHORT' && cmp >= 0) {
          stopBreached = true;
        }
      }
    }

    const isLatched = reportLatchedSymbols.has(symbol) || stopBreached;
    if (stopBreached) {
      reportLatchedSymbols.add(symbol);
    }

    let sentinelState = isObservationValidAndFresh ? 'ACTIVE_MONITORING' : item.sentinel_status;
    let sentinelReason = isObservationValidAndFresh ? null : item.sentinel_reason;

    if (uiState === 'disconnected' || report?.status === 'disconnected' || item.status === 'disconnected') {
      sentinelState = 'UNMONITORED_OFFLINE';
      sentinelReason = 'TRANSPORT_DISCONNECTED';
    } else if (isDocHidden) {
      sentinelState = 'UNMONITORED_OFFLINE';
      sentinelReason = 'TAB_HIDDEN_UNMONITORED';
    } else if (isLatched) {
      sentinelState = 'INVALIDATED';
      sentinelReason = sentinelReason || 'STOP_BREACH_DETECTED';
    } else if (isFutureQuote) {
      sentinelState = 'UNMONITORED_OFFLINE';
      sentinelReason = 'FUTURE_QUOTE_TIMESTAMP';
    } else if (isStaleQuote) {
      sentinelState = 'UNMONITORED_OFFLINE';
      sentinelReason = 'QUOTE_STALE_EXCEEDS_7S';
    } else if (item.status === 'stale') {
      sentinelState = 'UNMONITORED_OFFLINE';
      sentinelReason = 'SYMBOL_DATA_STALE';
    }

    const tacticsExpiryMs = report?.tactics_expiry_utc ? Date.parse(report.tactics_expiry_utc) : NaN;
    const isLocallyTacticsExpired =
      (!Number.isNaN(tacticsExpiryMs) && nowMs >= tacticsExpiryMs) ||
      report?.tactics_lifecycle === 'EXPIRED';

    const isSuppressed =
      uiState === 'disconnected' ||
      uiState === 'stale' ||
      uiState === 'empty' ||
      uiState === 'loading' ||
      report?.status === 'disconnected' ||
      report?.status === 'stale' ||
      report?.status === 'empty' ||
      report?.status === 'loading' ||
      item.status === 'stale' ||
      item.status === 'empty' ||
      item.status === 'disconnected' ||
      item.status === 'loading' ||
      sentinelState === 'INVALIDATED' ||
      isLocallyTacticsExpired ||
      !item.tactical_setup_eligible;

    let displayAction = item.tactical_action;
    if (isSuppressed) {
      displayAction = 'STAND_ASIDE';
    }

    let ribbonClass = 'stand-aside';
    let actionCn = '空仓观望';
    if (!isSuppressed) {
      if (displayAction === 'LONG_ACCUMULATE') {
        ribbonClass = 'long';
        actionCn = '逢低吸筹';
      } else if (displayAction === 'SHORT_DISTRIBUTE') {
        ribbonClass = 'short';
        actionCn = '做空派发';
      }
    }

    let badgeClass = 'active';
    let badgeText = 'ACTIVE_MONITORING';
    let badgeCn = '在轨盯盘';
    if (sentinelState === 'INVALIDATED') {
      badgeClass = 'invalidated';
      badgeText = 'INVALIDATED';
      badgeCn = '止损失效';
    } else if (sentinelState === 'UNMONITORED_OFFLINE') {
      badgeClass = 'offline';
      badgeText = sentinelReason || 'UNMONITORED_OFFLINE';
      badgeCn = '防守待机';
    }

    let symbolThesis = '4H 均线带处于纠缠震荡区间，缺乏清晰单边因果破位（盈亏比 < 1.5）。策略坚决执行空仓避险，防范频繁止损磨损本金。';
    if (uiState !== 'fresh' || item.status !== 'fresh' || isSuppressed) {
      symbolThesis = `[策略意见因数据状态 (${uiState.toUpperCase()}) 已抑制]`;
    } else if (item.tactical_setup_eligible) {
      if (displayAction === 'SHORT_DISTRIBUTE') {
        symbolThesis = `4H 阻力带实体承压，高位派发形态确立。模型计算下行盈亏比达 ${item.worst_band_rr || '--'}，具备清晰防守止损空间。`;
      } else if (displayAction === 'LONG_ACCUMULATE') {
        symbolThesis = `4H 支撑带企稳，底部吸筹结构确立。模型计算上行盈亏比达 ${item.worst_band_rr || '--'}，具备清晰防守止损空间。`;
      }
    } else if (effectiveMa20 && effectiveMa50) {
      symbolThesis = `4H 均线带 (SMA 20/50: ${effectiveMa20} / ${effectiveMa50}) 缺乏单边突破动能。维持空仓观望规约。`;
    } else if (item.reason === 'INSUFFICIENT_HISTORY_SAMPLE') {
      symbolThesis = '4H 历史样本处于因果闭合保护期，暂未收敛至 50-bar 极严因果窗口。策略启动资本保护机制，严禁盲目追单。';
    }

    // Drift calculation relative to benchmark close using exact decimal BigInt arithmetic (zero binary float)
    let driftText = '0.00%';
    const basePrice = effectiveBenchmarkPrice;
    if (basePrice && effectiveSpotPrice) {
      const toScaledBig = (s: string, scale: number = 6): bigint | null => {
        const trimmed = s.trim();
        if (!/^-?(0|[1-9]\d*)(\.\d+)?$/.test(trimmed)) return null;
        const isNeg = trimmed.startsWith('-');
        const unsigned = isNeg ? trimmed.slice(1) : trimmed;
        const [intPart, fracPart = ''] = unsigned.split('.');
        const paddedFrac = fracPart.slice(0, scale).padEnd(scale, '0');
        try {
          const b = BigInt(intPart + paddedFrac);
          return isNeg ? -b : b;
        } catch {
          return null;
        }
      };
      const cBig = toScaledBig(effectiveSpotPrice, 6);
      const bBig = toScaledBig(basePrice, 6);
      if (cBig !== null && bBig !== null && bBig > 0n) {
        const scaledPct = ((cBig - bBig) * 10000n) / bBig;
        const isNeg = scaledPct < 0n;
        const absPct = isNeg ? -scaledPct : scaledPct;
        const intPct = absPct / 100n;
        const fracPct = (absPct % 100n).toString().padStart(2, '0');
        const sign = isNeg ? '-' : '+';
        driftText = `${sign}${intPct}.${fracPct}%`;
      }
    }

    return {
      meta,
      effectiveSpotPrice,
      effectiveBenchmarkPrice,
      effectiveAtr14,
      effectiveMa20,
      effectiveMa50,
      effectiveSwingHigh,
      effectiveSwingLow,
      displayAction,
      actionCn,
      ribbonClass,
      badgeClass,
      badgeText,
      badgeCn,
      thesis: symbolThesis,
      isSuppressed,
      driftText,
      isObservationValidAndFresh,
      isStaleQuote,
      isFutureQuote,
      isLocallyTacticsExpired,
    };
  };

  // Render Master Deep-Dive Board for active selected asset
  const renderMasterDeepDiveBoard = (symbol: string) => {
    const item = report?.symbols?.[symbol];
    const evalData = evaluateSymbolItem(symbol, item);
    const {
      meta,
      effectiveSpotPrice,
      effectiveBenchmarkPrice,
      effectiveAtr14,
      effectiveMa20,
      effectiveMa50,
      effectiveSwingHigh,
      effectiveSwingLow,
      displayAction,
      actionCn,
      ribbonClass,
      badgeClass,
      badgeText,
      badgeCn,
      thesis,
      isSuppressed,
      driftText,
      isObservationValidAndFresh,
      isStaleQuote,
      isFutureQuote,
      isLocallyTacticsExpired,
    } = evalData;

    const isMasterOverlayFresh =
      uiState === 'fresh' &&
      report?.status === 'fresh' &&
      item?.status === 'fresh' &&
      item?.sentinel_overlay?.overlay_status === 'fresh' &&
      isObservationValidAndFresh &&
      !isStaleQuote &&
      !isFutureQuote &&
      !isLocallyTacticsExpired &&
      report?.tactics_lifecycle !== 'EXPIRED' &&
      (item?.quote_age_seconds === null || item?.quote_age_seconds === undefined || item?.quote_age_seconds <= 7);

    return (
      <div key={symbol} className={`strategist-card master-deep-dive-card ${ribbonClass}-card`}>
        {/* Dossier Header Banner */}
        <div className="dossier-top-banner">
          <div className="dossier-asset-meta">
            <span className="dossier-asset-icon">{meta.icon}</span>
            <div className="dossier-asset-titles">
              <div className="dossier-sym-title font-mono">
                <span className="dossier-sym-code">{symbol}</span>
                <span className="dossier-category-pill">{meta.category}</span>
              </div>
              <div className="dossier-timestamp-note font-mono text-muted">
                6H 定稿基准收盘时间: {report?.anchor_bar_close_utc || '2026-09-30T04:00:00Z'} (因果防偷看 W ≥ 3s 闭合柱)
              </div>
            </div>
          </div>

          <div className="dossier-price-cluster">
            <div className="price-tile benchmark-tile">
              <span className="price-tile-label font-mono">最新观测价格</span>
              <span className="spot-price benchmark-price font-mono">
                ${formatPresentationPrice(effectiveSpotPrice, symbol)}
              </span>
            </div>
            <div className="price-tile live-drift-tile">
              <span className="price-tile-label font-mono">定稿基准 / 实时偏离</span>
              <span className="live-drift-val font-mono">
                基准 ${formatPresentationPrice(effectiveBenchmarkPrice, symbol)}
                <span className={`drift-badge ${driftText.startsWith('+') ? 'drift-up' : driftText.startsWith('-') ? 'drift-down' : ''}`}>
                  {driftText}
                </span>
              </span>
            </div>
            <div className="price-tile basis-tile font-mono">
              <span className="price-tile-label">现货/永续基差</span>
              <span className="basis-val">
                {item?.basis_bps ? `${item.basis_bps} bps` : item?.perp_price ? `永续: $${formatPresentationPrice(item.perp_price, symbol)}` : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* Stage 076 Dual-Track Sentinel Overlay Banner */}
        {item?.sentinel_overlay && uiState !== 'disconnected' && report?.status !== 'disconnected' && (
          <div
            className="dossier-sentinel-overlay"
            style={{
              background: 'var(--bg-card)',
              border: '1px solid var(--border-card)',
              borderRadius: '8px',
              padding: '10px 14px',
              marginBottom: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    background:
                      item.sentinel_overlay.sentinel_state === 'DEFENSE_TRIGGERED'
                        ? 'rgba(239, 68, 68, 0.15)'
                        : item.sentinel_overlay.sentinel_state === 'BREAKOUT_TESTING'
                        ? 'rgba(234, 179, 8, 0.15)'
                        : item.sentinel_overlay.sentinel_state === 'VOLATILITY_SURGE'
                        ? 'rgba(168, 85, 247, 0.15)'
                        : item.sentinel_overlay.sentinel_state === 'BASIS_DISLOCATION'
                        ? 'rgba(249, 115, 22, 0.15)'
                        : 'rgba(34, 197, 94, 0.15)',
                    color:
                      item.sentinel_overlay.sentinel_state === 'DEFENSE_TRIGGERED'
                        ? '#ef4444'
                        : item.sentinel_overlay.sentinel_state === 'BREAKOUT_TESTING'
                        ? '#eab308'
                        : item.sentinel_overlay.sentinel_state === 'VOLATILITY_SURGE'
                        ? '#a855f7'
                        : item.sentinel_overlay.sentinel_state === 'BASIS_DISLOCATION'
                        ? '#f97316'
                        : '#22c55e',
                    border: `1px solid ${
                      item.sentinel_overlay.sentinel_state === 'DEFENSE_TRIGGERED'
                        ? 'rgba(239, 68, 68, 0.3)'
                        : item.sentinel_overlay.sentinel_state === 'BREAKOUT_TESTING'
                        ? 'rgba(234, 179, 8, 0.3)'
                        : item.sentinel_overlay.sentinel_state === 'VOLATILITY_SURGE'
                        ? 'rgba(168, 85, 247, 0.3)'
                        : item.sentinel_overlay.sentinel_state === 'BASIS_DISLOCATION'
                        ? 'rgba(249, 115, 22, 0.3)'
                        : 'rgba(34, 197, 94, 0.3)'
                    }`,
                  }}
                  className="font-mono"
                >
                  ⚡ 盘中哨兵: {isMasterOverlayFresh ? item.sentinel_overlay.sentinel_state : isStaleQuote || (item?.quote_age_seconds && item?.quote_age_seconds > 7) ? 'STALE' : item?.status === 'disconnected' ? 'DISCONNECTED' : isLocallyTacticsExpired || report?.tactics_lifecycle === 'EXPIRED' ? 'EXPIRED' : item.sentinel_overlay.sentinel_state}
                </span>
                <span style={{ fontSize: '12px', fontWeight: 600 }}>
                  {isMasterOverlayFresh
                    ? item.sentinel_overlay.sentinel_headline
                    : `[哨兵状态: ${(item.status === 'disconnected' ? 'DISCONNECTED' : isStaleQuote || (item.quote_age_seconds && item.quote_age_seconds > 7) ? 'STALE' : isLocallyTacticsExpired || report?.tactics_lifecycle === 'EXPIRED' ? 'EXPIRED' : item.sentinel_overlay.overlay_status).toUpperCase()}]`}
                </span>
              </div>
              <div className="font-mono" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Token: {item.sentinel_overlay.generation_token} | 状态: {item.sentinel_overlay.overlay_status}
              </div>
            </div>

            {isMasterOverlayFresh ? (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '8px',
                  background: 'rgba(0, 0, 0, 0.15)',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                }}
                className="font-mono"
              >
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>距4H前高阻力: </span>
                  <span style={{ fontWeight: 600 }}>{item.sentinel_overlay.distance_to_swing_high_pct ?? '--'}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>距4H前低支撑: </span>
                  <span style={{ fontWeight: 600 }}>{item.sentinel_overlay.distance_to_swing_low_pct ?? '--'}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>15m微观量比: </span>
                  <span style={{ fontWeight: 600 }}>{item.sentinel_overlay.micro_volume_ratio ? `${item.sentinel_overlay.micro_volume_ratio}x` : '--'}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>盘中偏向: </span>
                  <span style={{ fontWeight: 600 }}>{item.sentinel_overlay.intra_cycle_bias}</span>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                [哨兵微观指标仅在 fresh 状态下呈现]
              </div>
            )}
          </div>
        )}

        {/* Section 1 & Section 2 Split Grid */}
        <div className="dossier-main-split">
          {/* Left Column: AI 首席操盘战术执行单 */}
          <div className="dossier-col playbook-col">
            <div className="dossier-section-heading bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">★</span>
                <h4 className="dossier-section-title bilingual-card-title-zh">{CARD_TITLES_BILINGUAL.tacticalPlaybook.zh}</h4>
              </div>
              <span className="bilingual-card-subtitle-en">{CARD_TITLES_BILINGUAL.tacticalPlaybook.en}</span>
            </div>

            <div className={`tactical-ribbon ${ribbonClass} playbook-ribbon`}>
              <div className="action-text-cluster">
                <span className="tactical-action-text font-mono">{displayAction}</span>
                <span className="tactical-sub-label">{actionCn}</span>
              </div>
              {item?.worst_band_rr && (
                <span className="rr-badge font-mono">
                  战术盈亏比 R:R {item.worst_band_rr}
                </span>
              )}
            </div>

            {/* Geometry Ladder */}
            <div className="geometry-levels playbook-geometry font-mono">
              <div className="level-row">
                <span className="level-label">◈ 建议建仓观察区</span>
                <span className="level-val entry">
                  {displayAction !== 'STAND_ASIDE' && item?.entry_low && item?.entry_high
                    ? `[${formatPresentationPrice(item.entry_low, symbol)} ~ ${formatPresentationPrice(item.entry_high, symbol)}]`
                    : item?.entry_low && item?.entry_high
                    ? `[${formatPresentationPrice(item.entry_low, symbol)} ~ ${formatPresentationPrice(item.entry_high, symbol)}] (历史参考/已失效)`
                    : '暂无触发 (观望防守)'}
                </span>
              </div>
              <div className="level-row">
                <span className="level-label">▲ 硬性失效止损位</span>
                <span className="level-val stop">{item?.invalidation_stop ? formatPresentationPrice(item.invalidation_stop, symbol) : '无持仓止损'}</span>
              </div>
              <div className="level-row">
                <span className="level-label">◆ 目标兑现止盈位</span>
                <span className="level-val target">{item?.target_take_profit ? formatPresentationPrice(item.target_take_profit, symbol) : '等待突破'}</span>
              </div>
            </div>

            <div className="playbook-risk-note font-mono">
              <span className="risk-note-icon">🛡️</span>
              <div>
                <strong>建议风控仓位：</strong>
                {displayAction === 'STAND_ASIDE'
                  ? ' 0% (严格空仓防守，不承担非确定性风险)'
                  : ' 1.5% ~ 2.5% (单笔交易硬性止损风险必须限制在总本金 1% 以内)'}
              </div>
            </div>
          </div>

          {/* Right Column: 多级别立体时空研判矩阵 */}
          <div className="dossier-col mtf-col">
            <div className="dossier-section-heading bilingual-card-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span className="section-symbol">◈</span>
                <h4 className="dossier-section-title bilingual-card-title-zh">{CARD_TITLES_BILINGUAL.spatioTemporalMatrix.zh}</h4>
              </div>
              <span className="bilingual-card-subtitle-en">{CARD_TITLES_BILINGUAL.spatioTemporalMatrix.en}</span>
            </div>

            <div className="mtf-cards-stack font-mono">
              <div className="mtf-card macro-card">
                <div className="mtf-card-header">
                  <span className="mtf-tier-badge">宏观大级别 (Macro: 1D / 1W)</span>
                  <span className="mtf-role-tag">定趋势格局与仓位上限</span>
                </div>
                <p className="mtf-content">
                  {uiState !== 'fresh' || item?.status !== 'fresh'
                    ? `[宏观研判因数据状态 (${uiState.toUpperCase()}) 抑制]`
                    : item?.reason === 'INSUFFICIENT_HISTORY_SAMPLE'
                    ? '因果闭合保护中，大级别历史样本待收敛'
                    : '基准趋势跟踪中，恪守 6H 周期定稿宏观方向'}
                </p>
              </div>

              <div className="mtf-card meso-card">
                <div className="mtf-card-header">
                  <span className="mtf-tier-badge">中观波段结构 (Meso: 4H / 6H)</span>
                  <span className="mtf-role-tag highlight-cyan">本研判主战场 · 关键分水岭</span>
                </div>
                <p className="mtf-content">
                  {uiState !== 'fresh' || item?.status !== 'fresh'
                    ? `[中观波段因数据状态 (${uiState.toUpperCase()}) 抑制]`
                    : item?.reason || (displayAction === 'STAND_ASIDE' ? '4H 均线带收窄缠绕，未触发单边入场信号' : `4H 结构触发 ${actionCn}`)}
                </p>
              </div>

              <div className="mtf-card micro-card">
                <div className="mtf-card-header">
                  <span className="mtf-tier-badge">微观执行触发 (Micro: 1H / 15m)</span>
                  <span className="mtf-role-tag">量价背离与入场防滑点</span>
                </div>
                <p className="mtf-content">
                  {uiState !== 'fresh' || item?.status !== 'fresh'
                    ? `[微观执行因数据状态 (${uiState.toUpperCase()}) 抑制]`
                    : item?.invalidation_stop
                    ? `硬性失效止损位 $${formatPresentationPrice(item.invalidation_stop, symbol)}，防范假突破滑点`
                    : '无右侧突破信号，严格遵守空仓规约'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section 3: AI 首席分析师独家推演与资本保护规约 */}
        <div className="dossier-thesis-section">
          <div className="dossier-section-heading bilingual-card-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span className="section-symbol">◆</span>
              <h4 className="dossier-section-title bilingual-card-title-zh">{CARD_TITLES_BILINGUAL.analystThesis.zh}</h4>
            </div>
            <span className="bilingual-card-subtitle-en">{CARD_TITLES_BILINGUAL.analystThesis.en}</span>
          </div>
          <div className="ai-thesis-box expanded-thesis font-mono">
            <p className="thesis-text">{thesis}</p>
            <div className="invalidation-hypothesis-row">
              <span className="inv-badge">▲ 假设失效条件:</span>
              <span className="inv-text">
                {!isMasterOverlayFresh
                  ? `[失效条件因数据状态 (${(item?.status === 'disconnected' ? 'DISCONNECTED' : isStaleQuote || (item?.quote_age_seconds && item?.quote_age_seconds > 7) ? 'STALE' : isLocallyTacticsExpired || report?.tactics_lifecycle === 'EXPIRED' ? 'EXPIRED' : uiState.toUpperCase())}) 抑制]`
                  : item?.sentinel_overlay?.tactical_invalidation_rule
                  ? item.sentinel_overlay.tactical_invalidation_rule
                  : isSuppressed
                  ? `[失效条件因数据状态 (${uiState.toUpperCase()}) 抑制]`
                  : item?.invalidation_stop
                  ? `若 4H 实体收盘突破止损位 $${formatPresentationPrice(item.invalidation_stop, symbol)}，或到达 6H 周期时效（${report?.tactics_expiry_utc || 'W+6h'}），本假说立即自毁废弃。`
                  : effectiveMa20 && effectiveMa50
                  ? `若 4H 实体收盘放量突破均线带 [${formatPresentationPrice(effectiveMa20, symbol)} / ${formatPresentationPrice(effectiveMa50, symbol)}] 且动能持续，将重新评估入场机会。`
                  : '到达 6H 周期时效或触及因果边界，本假说立即自毁废弃。'}
              </span>
            </div>
          </div>
        </div>

        {/* Section 4: 量化指标环境底座与哨兵状态 */}
        <div className="dossier-quant-footer font-mono">
          <div className="indicators-row font-mono">
            <div className="indicator-item">
              <span className="ind-name">4H ATR-14 (纯 Decimal 波动率)</span>
              <span className="ind-val">{effectiveAtr14 ? formatPresentationPrice(effectiveAtr14, symbol) : '--'}</span>
            </div>
            <div className="indicator-item">
              <span className="ind-name">SMA 20 / 50 均线带</span>
              <span className="ind-val">
                {effectiveMa20 && effectiveMa50 ? `${formatPresentationPrice(effectiveMa20, symbol)} / ${formatPresentationPrice(effectiveMa50, symbol)}` : '--'}
              </span>
            </div>
            <div className="indicator-item">
              <span className="ind-name">20B Swing H/L 突破阈值</span>
              <span className="ind-val">
                {effectiveSwingHigh && effectiveSwingLow ? `${formatPresentationPrice(effectiveSwingHigh, symbol)} / ${formatPresentationPrice(effectiveSwingLow, symbol)}` : '--'}
              </span>
            </div>
            <div className="indicator-item">
              <span className="ind-name">因果时延合规审查</span>
              <span className={`ind-val ${report?.status === 'fresh' && item?.status === 'fresh' ? 'highlight-green' : ''}`}>
                {report?.status === 'fresh' && item?.status === 'fresh' ? 'W ≥ 3s VERIFIED' : 'W ≥ 3s PENDING'}
              </span>
            </div>
          </div>

          <div className="card-footer font-mono">
            <div className="sentinel-container">
              <div className={`sentinel-badge ${badgeClass}`} title={`${badgeText} (${badgeCn})`}>
                <span className="sentinel-dot" />
                <span>{badgeText}</span>
              </div>
              <span className="sentinel-cn-pill">{badgeCn}</span>
            </div>
            <div className="microstructure-note">
              {report?.generated_at_utc ? `定稿发布于: ${report.generated_at_utc}` : 'OI/FR: deferred (Stage 072)'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Mini Card for 7-asset matrix view
  const renderMiniCard = (symbol: string) => {
    const item = report?.symbols?.[symbol];
    const evalData = evaluateSymbolItem(symbol, item);
    const {
      meta,
      effectiveSpotPrice,
      displayAction,
      actionCn,
      ribbonClass,
      badgeClass,
      badgeText,
      badgeCn,
      thesis,
      isObservationValidAndFresh,
      isStaleQuote,
      isFutureQuote,
      isLocallyTacticsExpired,
    } = evalData;

    const isMiniOverlayFresh =
      uiState === 'fresh' &&
      report?.status === 'fresh' &&
      item?.status === 'fresh' &&
      item?.sentinel_overlay?.overlay_status === 'fresh' &&
      isObservationValidAndFresh &&
      !isStaleQuote &&
      !isFutureQuote &&
      !isLocallyTacticsExpired &&
      report?.tactics_lifecycle !== 'EXPIRED' &&
      (item?.quote_age_seconds === null || item?.quote_age_seconds === undefined || item?.quote_age_seconds <= 7);

    return (
      <div
        key={symbol}
        className={`strategist-card mini-card ${ribbonClass}-card ${selectedSymbol === symbol ? 'active-border' : ''}`}
        onClick={() => {
          setSelectedSymbol(symbol);
          setViewMode('deep_dive');
        }}
      >
        <div className="card-top-header">
          <div className="sym-badge-group">
            <span className="sym-icon">{meta.icon}</span>
            <div>
              <div className="symbol-name font-mono">{symbol}</div>
              <div className="symbol-cn-name">{meta.category}</div>
            </div>
          </div>
          <div className="price-box">
            <span className="spot-price font-mono">${formatPresentationPrice(effectiveSpotPrice, symbol)}</span>
            <div className="basis-tag font-mono">
              {item?.basis_bps ? `基差: ${item.basis_bps} bps` : item?.perp_price ? `永续: $${formatPresentationPrice(item.perp_price, symbol)}` : '现货锚点'}
            </div>
          </div>
        </div>

        <div className={`tactical-ribbon ${ribbonClass}`}>
          <div className="action-text-cluster">
            <span className="tactical-action-text font-mono">{displayAction}</span>
            <span className="tactical-sub-label">{actionCn}</span>
          </div>
          {item?.worst_band_rr && (
            <span className="rr-badge font-mono">
              赔率 R:R {item.worst_band_rr}
            </span>
          )}
        </div>

        {item?.sentinel_overlay && uiState !== 'disconnected' && report?.status !== 'disconnected' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              fontSize: '10px',
              padding: '3px 8px',
              borderRadius: '4px',
              background: 'rgba(0, 0, 0, 0.14)',
              border: '1px solid var(--border-card)',
              marginTop: '4px',
              marginBottom: '4px',
            }}
            className="font-mono"
          >
            <span
              style={{
                fontWeight: 700,
                color:
                  item.sentinel_overlay.sentinel_state === 'DEFENSE_TRIGGERED'
                    ? '#ef4444'
                    : item.sentinel_overlay.sentinel_state === 'BREAKOUT_TESTING'
                    ? '#eab308'
                    : item.sentinel_overlay.sentinel_state === 'VOLATILITY_SURGE'
                    ? '#a855f7'
                    : item.sentinel_overlay.sentinel_state === 'BASIS_DISLOCATION'
                    ? '#f97316'
                    : '#22c55e',
              }}
            >
              ● 哨兵: {isMiniOverlayFresh ? item.sentinel_overlay.sentinel_state : isStaleQuote || (item?.quote_age_seconds && item?.quote_age_seconds > 7) ? 'STALE' : item?.status === 'disconnected' ? 'DISCONNECTED' : isLocallyTacticsExpired || report?.tactics_lifecycle === 'EXPIRED' ? 'EXPIRED' : item.sentinel_overlay.sentinel_state}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              {isMiniOverlayFresh
                ? `高 ${item.sentinel_overlay.distance_to_swing_high_pct ?? '--'} | 低 ${item.sentinel_overlay.distance_to_swing_low_pct ?? '--'}`
                : `[${(item?.status === 'disconnected' ? 'DISCONNECTED' : isStaleQuote || (item?.quote_age_seconds && item?.quote_age_seconds > 7) ? 'STALE' : isLocallyTacticsExpired || report?.tactics_lifecycle === 'EXPIRED' ? 'EXPIRED' : item.sentinel_overlay.overlay_status).toUpperCase()}]`}
            </span>
          </div>
        )}

        <div className="ai-thesis-box">
          <div className="thesis-title">◈ AI 分析师决策意见</div>
          <p className="thesis-text">{thesis}</p>
        </div>

        <div className="geometry-levels font-mono">
          <div className="level-row">
            <span className="level-label">入场观察区</span>
            <span className="level-val entry">
              {displayAction !== 'STAND_ASIDE' && item?.entry_low && item?.entry_high
                ? `[${formatPresentationPrice(item.entry_low, symbol)} ~ ${formatPresentationPrice(item.entry_high, symbol)}]`
                : item?.entry_low && item?.entry_high
                ? `[${formatPresentationPrice(item.entry_low, symbol)} ~ ${formatPresentationPrice(item.entry_high, symbol)}] (失效参考)`
                : '--'}
            </span>
          </div>
          <div className="level-row">
            <span className="level-label">失效止损位</span>
            <span className="level-val stop">{item?.invalidation_stop ? formatPresentationPrice(item.invalidation_stop, symbol) : '--'}</span>
          </div>
          <div className="level-row">
            <span className="level-label">目标止盈位</span>
            <span className="level-val target">{item?.target_take_profit ? formatPresentationPrice(item.target_take_profit, symbol) : '--'}</span>
          </div>
        </div>

        <div className="card-footer font-mono">
          <div className="sentinel-container">
            <div className={`sentinel-badge ${badgeClass}`} title={`${badgeText} (${badgeCn})`}>
              <span className="sentinel-dot" />
              <span>{badgeText}</span>
            </div>
            <span className="sentinel-cn-pill">{badgeCn}</span>
          </div>
          <button className="view-deep-btn font-mono" onClick={(e) => {
            e.stopPropagation();
            setSelectedSymbol(symbol);
            setViewMode('deep_dive');
          }}>
            深度推演看板 →
          </button>
        </div>
      </div>
    );
  };

  const narrativeText = report?.narrative || null;
  const narrativeReason = report?.provenance?.narrative_reason || report?.provenance?.reason || 'UNAVAILABLE';
  const narrativeSource = report?.narrative_source || report?.provenance?.narrative_source || 'DETERMINISTIC_DESK_ENGINE';
  const factIds: string[] = Array.isArray(report?.provenance?.fact_ids) ? report.provenance.fact_ids : [];

  return (
    <div className="view-panel active crypto-strategist-view" id="view-crypto-strategist" role="tabpanel">
      {/* 1. TSO 2.0 Standard Aligned Compact Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <h2 className="bilingual-header-title-zh">
              <span className="section-symbol" style={{ color: 'var(--accent-cyan)' }}>◈</span>
              <span>{CRYPTO_BILINGUAL.strategist.zh}</span>
            </h2>
            <span className="terminal-badge">6H 战术推演</span>
          </div>
          <span className="bilingual-header-subtitle-en">{CRYPTO_BILINGUAL.strategist.en}</span>
          <p className="view-subtitle font-mono" style={{ margin: '3px 0 0 0' }}>
            AI 首席战术策略师 · 4H 闭合因果几何推演 · 纯 Decimal 精度 · 哨兵在轨盯盘 · 严格因果时延 W &gt;= 3s
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Cadence Resolution Pill Selector */}
          <div className="cadence-selector-group font-mono">
            <span className="cadence-lbl">周期解析度:</span>
            {CADENCE_OPTIONS.map((c) => (
              <button
                key={c.id}
                className={`cadence-btn ${selectedCadence === c.id ? 'active' : ''}`}
                onClick={() => setSelectedCadence(c.id)}
                title={`${c.description} (${c.status === 'active' ? '当前生效' : '架构就绪'})`}
              >
                <span>{c.shortLabel}</span>
                {c.status === 'ready' && <span className="cadence-ready-tag">预留</span>}
              </button>
            ))}
          </div>

          {renderTelemetryBadge()}

          <button
            className="toggle-btn btn-refresh-strategist font-mono"
            onClick={fetchReport}
            disabled={isRefreshing}
            title="点击同步最新 6H 战术推演数据"
          >
            {isRefreshing ? '↻ 同步中...' : '↻ 刷新报告'}
          </button>
        </div>
      </div>

      {/* 2. Cadence Timeline & Session Archive Navigator (历史记录与周期导航) */}
      <div className="timeline-archive-bar font-mono">
        <div className="timeline-header-block">
          <Calendar size={13} style={{ color: 'var(--accent-cyan)' }} />
          <span className="timeline-title-text">研判期次与时效窗口:</span>
          <span className="date-badge">
            {report?.anchor_bar_close_utc ? `${report.anchor_bar_close_utc.replace('T', ' ').replace('Z', '')} UTC` : '当前周期定稿 (UTC)'}
          </span>
          <span className="session-selected-hint">
            [当前生效: {currentSessionSlotId} (定稿时效至 {report?.tactics_expiry_utc ? report.tactics_expiry_utc.slice(11, 19) + ' UTC' : 'W+6h'})]
          </span>
        </div>

        <div className="timeline-sessions-rail">
          {sessionSlots.map((slot) => {
            const isCurrent = slot.id === currentSessionSlotId;
            const statusClass = isCurrent ? 'active' : 'pending';
            const statusLabel = isCurrent ? '当前定稿生效期' : '历史离线未就绪';

            return (
              <button
                key={slot.id}
                className={`session-pill ${statusClass} ${isCurrent ? 'selected' : ''}`}
                disabled={!isCurrent}
                title={isCurrent ? `当前定稿生效期: ${slot.label}` : `历史期次回溯未就绪`}
              >
                <span className="session-index font-bold">{slot.id}</span>
                <span className="session-time">{slot.label}</span>
                <span className={`session-status-tag ${statusClass}`}>{statusLabel}</span>
              </button>
            );
          })}
        </div>

        <div className="countdown-box">
          <span className="countdown-label">定稿时效状态:</span>
          <span className="countdown-val font-bold highlight-amber">
            {report?.tactics_expiry_utc ? `有效期至 ${report.tactics_expiry_utc.slice(11, 16)} UTC` : '实盘在轨监控中'}
          </span>
        </div>
      </div>

      {/* 3. Compact Single-Line Macro Status Ribbon (32px) */}
      {report && (
        <div className="brief-sub-bar font-mono strategist-sub-bar">
          <div className="sub-bar-item">
            <span className="sub-bar-lbl">6H 周期窗口:</span>
            <span className="sub-bar-val font-bold">{report.window_id}</span>
          </div>
          <div className="sub-bar-divider">|</div>
          <div className="sub-bar-item">
            <span className="sub-bar-lbl">因果锚点 (Ak):</span>
            <span className="sub-bar-val">{report.anchor_bar_close_utc}</span>
          </div>
          <div className="sub-bar-divider">|</div>
          <div className="sub-bar-item">
            <span className="sub-bar-lbl">战术状态:</span>
            <span className={`sub-bar-pill pill-${report.tactics_lifecycle.toLowerCase()}`}>
              {report.tactics_lifecycle === 'ACTIVE' ? '● 生效中 ACTIVE' : '◌ 已到期 EXPIRED'}
            </span>
          </div>
          <div className="sub-bar-divider">|</div>
          <div className="sub-bar-item">
            <span className="sub-bar-lbl">主定调场景:</span>
            <span className="sub-bar-val highlight-cyan">{report.primary_scenario || 'TREND_CONTINUATION'}</span>
          </div>
          <div className="sub-bar-divider">|</div>
          <div className="sub-bar-item">
            <span className="sub-bar-lbl">次级对冲:</span>
            <span className="sub-bar-val text-muted">{report.contingent_scenario || 'MEAN_REVERSION_DEFENSE'}</span>
          </div>
          <div className="sub-bar-divider">|</div>
          <div className="sub-bar-item">
            <span className="sub-bar-lbl">有效战术机会:</span>
            <span className="sub-bar-val font-bold highlight-amber">{activeSetupsCount} / 7 标的</span>
          </div>
          <div className="sub-bar-spacer" />
          <span className="adr-cite text-muted">SOP-06 SINGLE PIPELINE</span>
        </div>
      )}

      {renderBanner()}

      {/* 4. Bound Fact IDs & Deterministic Narrative Bar */}
      <section className="strategist-narrative-panel font-mono" aria-label="Deterministic Desk Narrative">
        <div className="narrative-meta-row">
          <span className="narrative-badge-src">推演引擎: {narrativeSource}</span>
          <span className="narrative-badge-fact">
            主场景: <strong>{report?.primary_scenario || 'TREND_CONTINUATION'}</strong>
          </span>
          <span className="narrative-badge-fact">
            对冲场景: <strong>{report?.contingent_scenario || 'MEAN_REVERSION_DEFENSE'}</strong>
          </span>
          {report?.is_updating && (
            <span className="narrative-updating-tag pulse">● RECALCULATING_WINDOW...</span>
          )}
        </div>
        <div className="narrative-body-text">
          {uiState === 'fresh' && narrativeText
            ? narrativeText
            : `Narrative suppressed (${uiState !== 'fresh' ? uiState.toUpperCase() : narrativeReason})`}
        </div>
        {factIds.length > 0 && uiState === 'fresh' && (
          <div className="narrative-fact-ids">
            <span className="fact-label">因果绑定事实链:</span>
            {factIds.map((fid) => (
              <span key={fid} className="fact-id-chip">{fid}</span>
            ))}
          </div>
        )}
      </section>

      {/* 5. Asset Command Rail (标的切换导航栏) */}
      <div className="asset-command-rail font-mono">
        <div className="rail-left-controls">
          <button
            className="rail-arrow-btn"
            title="快捷切换至上一品种 (或按键盘 ←)"
            onClick={() => {
              setSelectedSymbol((prev) => {
                const idx = CORE_SYMBOLS.indexOf(prev);
                const nextIdx = (idx - 1 + CORE_SYMBOLS.length) % CORE_SYMBOLS.length;
                return CORE_SYMBOLS[nextIdx];
              });
            }}
          >
            ‹
          </button>
          <span className="rail-label">标的快速选择:</span>
        </div>

        <div className="asset-tabs-container">
          {CORE_SYMBOLS.map((sym) => {
            const item = report?.symbols?.[sym];
            const meta = SYMBOL_META[sym];
            const isSelected = selectedSymbol === sym;
            const isActionable = isSymbolEffectiveActionable(sym, item);

            return (
              <button
                key={sym}
                className={`asset-tab-btn ${isSelected ? 'active' : ''} ${isActionable ? 'has-setup' : ''}`}
                onClick={() => {
                  setSelectedSymbol(sym);
                }}
              >
                <span className="tab-icon">{meta?.icon || '◇'}</span>
                <span className="tab-sym">{sym.replace('USDT', '')}</span>
                {isActionable ? (
                  <span className="tab-action-pill highlight-gold">
                    ★ {item?.tactical_action === 'SHORT_DISTRIBUTE' ? '派发空' : '吸筹多'} R:{item?.worst_band_rr || '--'}
                  </span>
                ) : (
                  <span className="tab-action-pill stand-aside-pill">
                    观望
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="rail-right-controls">
          <button
            className="rail-arrow-btn"
            title="快捷切换至下一品种 (或按键盘 →)"
            onClick={() => {
              setSelectedSymbol((prev) => {
                const idx = CORE_SYMBOLS.indexOf(prev);
                const nextIdx = (idx + 1) % CORE_SYMBOLS.length;
                return CORE_SYMBOLS[nextIdx];
              });
            }}
          >
            ›
          </button>

          {/* View Mode Toggle */}
          <div className="view-mode-toggle">
            <button
              className={`mode-btn ${viewMode === 'deep_dive' ? 'active' : ''}`}
              onClick={() => setViewMode('deep_dive')}
              title="聚焦当前选中标的的深度时空研判与交易执行单"
            >
              ◈ 深度大看板
            </button>
            <button
              className={`mode-btn ${viewMode === 'matrix' ? 'active' : ''}`}
              onClick={() => setViewMode('matrix')}
              title="7 大标的全景矩阵概览"
            >
              ☷ 全景矩阵
            </button>
          </div>
        </div>
      </div>

      {/* 6. Primary Viewport: Master Deep-Dive Board OR 7-Asset Matrix Grid */}
      <main className="strategist-main-stage">
        {viewMode === 'deep_dive' ? (
          <div className="deep-dive-container">
            {renderMasterDeepDiveBoard(selectedSymbol)}
          </div>
        ) : (
          <div className="strategist-grid matrix-mode">
            {CORE_SYMBOLS.map((sym) => renderMiniCard(sym))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CryptoStrategistView;
